import React from 'react';
import { Table, TableColumn, Submission, Project } from '../../../types/domain';
import { FilterRule, RBACPermissionTier } from '../../../types/grid';
import { DataGrid } from '../../../components/grid/DataGrid';
import { EndpointsTab } from '../../../components/datastore/EndpointsTab';
import { RulesTab } from '../../../components/datastore/RulesTab';
import { WebhooksTab } from '../../../components/datastore/WebhooksTab';
import { AnalyticsTab } from '../../../components/datastore/AnalyticsTab';
import { SettingsTab } from '../../../components/datastore/SettingsTab';
import { SchemaTab } from '../../../components/datastore/SchemaTab';
import { SpamProtectionTab } from '../../../components/datastore/SpamProtectionTab';
import { FilesTab } from '../../../components/datastore/FilesTab';
import { WorkspaceTab } from '../hooks/useTableViewState';

interface TableTabContentProps {
  activeTab: WorkspaceTab;
  resolvedTable: Table;
  currentProject: Project;
  columns: TableColumn[];
  rows: Submission[];
  totalRows: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onRowsUpdated: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  permissionTier: RBACPermissionTier;
  isLoading: boolean;
  canEdit: boolean;
  isFullTier: boolean;
  onEditingChange: (isEditing: boolean) => void;
  onServerSearchChange: (search: string, idFilter?: string) => void;
  filterRules: FilterRule[];
  onFilterChange: (rules: FilterRule[]) => void;
  unfilteredTotal: number | null;
  sortCol: string;
  sortDir: 'asc' | 'desc';
  sortRules: Array<{ id: string; desc: boolean }>;
  onSortChange: (sortCol: string, sortDir: 'asc' | 'desc', allSorts?: Array<{ id: string; desc: boolean }>) => void;
  onNavigateEndpoints: () => void;
  setColumns: (cols: TableColumn[]) => void;
  onTableUpdated: (table: Table) => void;
  settingsSubTab: 'general' | 'danger';
  onSettingsSubTabChange: (tab: 'general' | 'danger') => void;
  headerActionEl: HTMLDivElement | null;
}

export const TableTabContent: React.FC<TableTabContentProps> = ({
  activeTab,
  resolvedTable,
  currentProject,
  columns,
  rows,
  totalRows,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRowsUpdated,
  onRefresh,
  isRefreshing,
  permissionTier,
  isLoading,
  canEdit,
  isFullTier,
  onEditingChange,
  onServerSearchChange,
  filterRules,
  onFilterChange,
  unfilteredTotal,
  sortCol,
  sortDir,
  sortRules,
  onSortChange,
  onNavigateEndpoints,
  setColumns,
  onTableUpdated,
  settingsSubTab,
  onSettingsSubTabChange,
  headerActionEl,
}) => {
  return (
    <div
      className={
        activeTab === 'data'
          ? 'flex-1 flex flex-col min-h-0 min-w-0 w-full max-w-full overflow-hidden'
          : 'w-full min-w-0 max-w-full'
      }
    >
      {activeTab === 'data' && (
        <div className="h-full w-full min-w-0 max-w-full flex flex-col">
          <DataGrid
            projectId={currentProject.id}
            tableId={resolvedTable.id}
            tableSlug={resolvedTable.slug || 'submissions'}
            ingestToken={resolvedTable.ingest_token || resolvedTable.slug || resolvedTable.id}
            columns={columns}
            rows={rows}
            totalRows={totalRows}
            page={page}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowsUpdated={onRowsUpdated}
            onRefresh={onRefresh}
            isRefreshing={isRefreshing}
            userTier={permissionTier}
            isLoading={isLoading}
            onEditingChange={onEditingChange}
            onServerSearchChange={onServerSearchChange}
            filterRules={filterRules}
            onFilterChange={onFilterChange}
            unfilteredTotal={unfilteredTotal}
            sortCol={sortCol}
            sortDir={sortDir}
            sortRules={sortRules}
            onSortChange={onSortChange}
            onNavigateEndpoints={onNavigateEndpoints}
            persistenceKey={`table_${resolvedTable.id}`}
          />
        </div>
      )}

      {activeTab === 'files' && (
        <FilesTab table={resolvedTable} project={currentProject} canEdit={canEdit} />
      )}

      {activeTab === 'schema' && (
        <SchemaTab
          table={resolvedTable}
          project={currentProject}
          canEdit={isFullTier}
          columns={columns}
          onColumnsUpdated={setColumns}
          onTableUpdated={onTableUpdated}
        />
      )}

      {activeTab === 'endpoints' && (
        <EndpointsTab table={resolvedTable} project={currentProject} columns={columns} />
      )}

      {activeTab === 'rules' && (
        <RulesTab
          table={resolvedTable}
          project={currentProject}
          canEdit={isFullTier}
          columns={columns}
          headerActionContainer={headerActionEl}
        />
      )}

      {activeTab === 'webhooks' && (
        <WebhooksTab
          table={resolvedTable}
          project={currentProject}
          canEdit={isFullTier}
          headerActionContainer={headerActionEl}
        />
      )}

      {activeTab === 'spam' && (
        <SpamProtectionTab
          table={resolvedTable}
          project={currentProject}
          canEdit={isFullTier}
          onTableUpdated={onTableUpdated}
        />
      )}

      {activeTab === 'analytics' && (
        <AnalyticsTab
          table={resolvedTable}
          project={currentProject}
          headerActionContainer={headerActionEl}
        />
      )}

      {activeTab === 'settings' && (
        <SettingsTab
          table={resolvedTable}
          project={currentProject}
          canEdit={isFullTier}
          onTableUpdated={onTableUpdated}
          activeSubTab={settingsSubTab}
          onSubTabChange={onSettingsSubTabChange}
        />
      )}
    </div>
  );
};
